import { ClearIcon, UploadIcon } from 'components/icons'
import React, { useRef, useState } from 'react'

interface QuickDataEntryProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  acceptFileTypes?: string
  height?: string
  isError?: boolean
}

const QuickDataEntry: React.FC<QuickDataEntryProps> = ({
  value,
  onChange,
  placeholder,
  acceptFileTypes = '.csv,.txt',
  height = 'h-[250px]',
  isError = false,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [warningMessage, setWarningMessage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropAreaRef = useRef<HTMLDivElement>(null)

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!dropAreaRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    setWarningMessage(null)

    if (e.dataTransfer?.files?.length) {
      const file = e.dataTransfer.files[0]
      handleFile(file)
    }
  }

  // Process uploaded file
  const handleFile = (file: File) => {
    setWarningMessage(null)

    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    const acceptedTypes = acceptFileTypes.split(',').map((type) => type.trim().replace('.', '').toLowerCase())

    if (fileExtension && !acceptedTypes.includes(fileExtension)) {
      setWarningMessage(`Invalid file type: .${fileExtension}. Supported types: ${acceptedTypes.join(', ')}`)
      return
    }

    // Read file
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      if (content) {
        onChange(content)
      }
    }
    reader.readAsText(file)
  }

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
    }
    e.target.value = '' // Reset file input
  }

  // Handle text change - immediately update parent via onChange
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    onChange(newValue) // Immediately update parent component
    setWarningMessage(null)
  }

  // Clear text
  const handleClear = () => {
    onChange('')
    setWarningMessage(null)
  }

  // Trigger file upload
  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className='bg-white shadow-sm'>
      {/* Drop area */}
      <div
        ref={dropAreaRef}
        className={`
          relative border-2 border-dashed rounded-lg overflow-hidden transition-all
          ${isDragging ? 'border-yellow-400 bg-yellow-50' : isError ? 'border-red-300' : 'border-gray-300'}
          ${value ? 'hover:border-gray-400' : 'hover:border-yellow-300'}
          focus-within:border-yellow-400 focus-within:ring-1 focus-within:ring-yellow-400
        `}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Textarea */}
        <textarea
          value={value}
          onChange={handleTextChange}
          placeholder={placeholder}
          className={`
            w-full ${height} p-4 resize-none outline-none bg-transparent
            ${isDragging ? 'opacity-50' : 'opacity-100'}
          `}
        />

        {/* Empty state */}
        {!value && !isDragging && (
          <div className='absolute inset-0 flex flex-col items-center justify-center pointer-events-none'>
            <div className='flex flex-col items-center text-center'>
              <UploadIcon />
              <p className='text-gray-400 mt-2 mb-1'>Type or paste rows of data here...</p>
              <p className='text-yellow-500 font-medium'>Drag & drop file here</p>
              <p className='text-gray-400 text-sm mt-1'>
                or{' '}
                <span className='text-yellow-500 cursor-pointer pointer-events-auto' onClick={handleUploadClick}>
                  click to upload
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Drag overlay */}
        {isDragging && (
          <div className='absolute inset-0 bg-yellow-50 bg-opacity-90 flex items-center justify-center z-20 pointer-events-none'>
            <div className='flex flex-col items-center text-center'>
              <UploadIcon />
              <p className='text-yellow-600 font-medium text-lg mt-3'>Drop file to upload</p>
            </div>
          </div>
        )}

        {/* Clear button */}
        {value && (
          <button
            onClick={handleClear}
            className='absolute bottom-3 right-3 bg-white bg-opacity-80 rounded-full p-1 hover:bg-gray-100 transition-colors'
            aria-label='Clear text'
          >
            <ClearIcon onClick={undefined} />
          </button>
        )}
      </div>

      {/* Warning message */}
      {warningMessage && <p className='text-red-500 text-sm mt-2'>{warningMessage}</p>}

      {/* Hidden file input */}
      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept={acceptFileTypes}
        className='hidden'
      />
    </div>
  )
}

export default QuickDataEntry
